import React, { useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { useContentViewerModalState } from '../../state/ContentViewerModalStateProvider'
import { DNABox, DNAButton, DNAChip, DNAText, Hoverable, Iffy, luxColors } from '@alucio/lux-ui';
import { AssociatedFileORM, getAssociatedFileTitle } from 'src/types/types';
import { AssociatedFileType, AttachedFile, Document, DocumentStatus } from '@alucio/aws-beacon-amplify/src/models';
import { useAllDocumentsInstance } from 'src/state/redux/selector/document';
import { useDispatch } from 'react-redux';
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal';
import { downloadContentFromCloudfront } from 'src/utils/loadCloudfrontAsset/loadCloudfrontAsset';
import DNADocumentChip from 'src/components/DNA/Document/DNADocumentChip';
import { useDNADocumentActions } from 'src/components/DNA/Document/DNADocument.actions'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';

import mergeStyles from '@alucio/lux-ui/src/components/util/mergeStyles';

const styles = StyleSheet.create({
  fileRow: {
    paddingBottom: 16,
    paddingTop: 16,
    borderTopColor: colors['color-flat-300'],
    borderTopWidth: 1,
  },
  hoveredRow: {
    backgroundColor: luxColors.mainContainer.primary,
  },
})

interface FileRowProps {
  file: AssociatedFileORM,
}

const AssociatedDocumentRow = (props: FileRowProps) => {
  const { file } = props
  const { activeDocVersion } = useContentViewerModalState()
  const documentActions = useDNADocumentActions()
  const dispatch = useDispatch()
  const { setActiveDocVersionAndSlide } = useContentViewerModalState()
  const [documentORM] = useAllDocumentsInstance({ filter: { model: { id: file.file?.id } } })
  const [isHovered, setIsHovered] = useState<boolean>(false);

  function toggleHover(): void {
    setIsHovered(currVal => !currVal);
  }

  const onFileSelected = () => {
    const docVersion = documentORM.relations.version.latestUsableDocumentVersion
    dispatch(
      contentPreviewModalActions.changeDocument({
        documentVersionId: docVersion.model.id,
        addToHistory: true,
      }))
    setActiveDocVersionAndSlide(docVersion, 1)
    analytics?.track('DOCUMENT_AF_OPENED', {
      action: 'AF_OPENED',
      attachedContentId: file.model.attachmentId,
      category: 'DOCUMENT',
      documentId: activeDocVersion.model.documentId,
      documentVersionId: activeDocVersion.model.id,
    });
  }
  return (
    <Pressable onPress={onFileSelected}>
      <Hoverable onHoverIn={toggleHover} onHoverOut={toggleHover}>
        <DNABox
          fill
          spacing="between"
          alignY="center"
          style={mergeStyles(undefined, styles.fileRow, [styles.hoveredRow, isHovered])}
        >
          {/* TITLE + CHIPS */}
          <DNABox
            appearance="col"
            spacing="small"
          >
            <DNAText>{getAssociatedFileTitle(file)}</DNAText>
            <DNABox spacing="small">
              <DNAChip appearance="tag" status="dark">{documentORM.model.type}</DNAChip>
              <DNADocumentChip
                item={documentORM}
                variant="purpose"
              />
            </DNABox>
          </DNABox>

          <Iffy is={documentORM.meta.permissions.MSLDownload}>
            {/* DOWNLOAD BUTTON */}
            <DNAButton
              appearance="ghost"
              status="subtle"
              iconLeft="download"
              onPress={documentActions.download(documentORM)}
            />
          </Iffy>
        </DNABox>
      </Hoverable>
    </Pressable>
  )
}

const AttachedFileRow = (props: FileRowProps) => {
  const { file } = props
  const { activeDocVersion } = useContentViewerModalState();
  const attachedFile = file.file as AttachedFile;
  const [isHovered, setIsHovered] = useState<boolean>(false);

  function toggleHover(): void {
    setIsHovered(currVal => !currVal);
  }

  const onFileSelected = () => {
    downloadContentFromCloudfront(attachedFile.srcFile.key, attachedFile.srcFileName, attachedFile.type)
    analytics?.track('DOCUMENT_AF_DOWNLOAD', {
      action: 'AF_DOWNLOAD',
      attachedContentId: file.model.attachmentId,
      category: 'DOCUMENT',
      documentId: activeDocVersion.relations.document.model.id,
      documentVersionId: activeDocVersion.model.id,
      type: file.model.type,
    });
  }
  return (
    <Pressable onPress={onFileSelected}>
      <Hoverable onHoverIn={toggleHover} onHoverOut={toggleHover}>
        <DNABox
          fill
          spacing="between"
          alignY="center"
          style={mergeStyles(undefined, styles.fileRow, [styles.hoveredRow, isHovered])}
        >
          {/* TITLE + CHIPS */}
          <DNABox
            appearance="col"
            spacing="small"
          >
            <DNAText>{attachedFile.title}</DNAText>
            <DNAChip
              appearance="tag"
              status="dark"
              style={{ alignSelf: 'flex-start' }}
            >
              {attachedFile.type}
            </DNAChip>
          </DNABox>

          {/* DOWNLOAD BUTTON */}
          <DNAButton
            appearance="ghost"
            status="subtle"
            iconLeft="download"
            onPress={onFileSelected}
          />
        </DNABox>
      </Hoverable>
    </Pressable>
  )
}

function AssociatedFilesSideBar() {
  const { activeDocVersion } = useContentViewerModalState()

  const versionElements = activeDocVersion.relations.associatedFiles
    .filter(associatedFile => {
      if (associatedFile.model.type !== AssociatedFileType.DOCUMENT) {
        return true;
      }

      return (associatedFile.file as Document).status === DocumentStatus.PUBLISHED;
    })
    .map(assocFile => {
      if (assocFile.model.type === AssociatedFileType.DOCUMENT) {
        return <AssociatedDocumentRow file={assocFile} key={assocFile.model.id} />
      } else if (assocFile.model.type === AssociatedFileType.ATTACHED_FILE) {
        return <AttachedFileRow file={assocFile} key={assocFile.model.id} />
      }
    })

  return (
    <DNABox appearance="col">
      {versionElements}
    </DNABox>
  )
}

export default AssociatedFilesSideBar;
